import './ManageTeam.scss'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import React, { useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import FormattedDate from '../../components/FormattedDate'
import { ActionButton } from '../../components/DataTable/ActionButton'
import { Col, Container, Dropdown, Row } from 'react-bootstrap'
import { CustomDropdownItem } from '../../components/Dropdown/CustomDropdownItem'

import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'

import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { Roles } from '../../components/Roles/Roles'
import { CrossAccountGrantOfGranter, CrossAccountGrantStatus, useListCrossAccountGrantsOfAccountQuery } from '../../store/crossAccount/crossAccount'
import { DeleteGrantModal } from './DeleteGrantModal'
import { EditGrantModal } from './EditGrantModal'

export const PendingGrantsTab = () => {
  const { grantId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedGrantId, setSelectedGrantId] = useState<string | null>(grantId)
  const [showEditGrantModal, setShowEditGrantModal] = useState<boolean>(modal === 'edit')
  const [showDeleteGrantModal, setShowDeleteGrantModal] = useState<boolean>(false)
  const {
    data,
    isFetching,
    refetch: refetchGrants,
  } = useListCrossAccountGrantsOfAccountQuery(
    {
      accountId: accountId,
      statuses: [CrossAccountGrantStatus.PENDING, CrossAccountGrantStatus.REJECTED],
      pageRequest: { page: page - 1, size: perPage },
    },
    { refetchOnMountOrArgChange: true },
  )

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const handleOnClickEditGrant = (grant: CrossAccountGrantOfGranter) => {
    setSelectedGrantId(grant.id)
    setShowEditGrantModal(true)
    navigate(`/account/manage-team/grants/${grant.id}/edit`, { replace: true })
  }

  const handleOnClickDeleteGrant = (user: CrossAccountGrantOfGranter) => {
    setSelectedGrantId(user.id)
    setShowDeleteGrantModal(true)
  }

  const handleEditGrantModalHide = () => {
    setShowEditGrantModal(false)
    setSelectedGrantId(null)
    refetchGrants()
    navigate(`/account/manage-team/cross-account-members`, { replace: true })
  }

  const handleDeleteGrantModalHide = () => {
    setShowDeleteGrantModal(false)
    setSelectedGrantId(null)
    refetchGrants()
  }

  const columns: TableColumn<CrossAccountGrantOfGranter>[] = [
    {
      name: 'Name',
      selector: (row) => row.granteeDisplayName,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Account Name',
      selector: (row) => row.granteeAccountName,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Role',
      selector: (row) => JSON.stringify(row.roleIds),
      sortable: true,
      wrap: true,
      cell: (row) => <Roles userRoleIds={row.roleIds} />,
    },
    {
      name: 'Date added',
      selector: (row) => (row.createdAt ? formatDate(row.createdAt) : ''),
      sortable: true,
      cell: (row) => <FormattedDate date={row.createdAt ? formatDate(row.createdAt) : ''} />,
      hide: 992,
      grow: 2,
    },
    {
      name: 'status',
      selector: (row) => (row.status ? row.status : ''),
      sortable: true,
      conditionalCellStyles: [
        {
          when: (row) => row.status === 'active',
          style: {
            color: 'var(--zocks-success)',
          },
        },
        {
          when: (row) => row.status === 'pending',
          style: {
            color: 'var(--zocks-warning)',
          },
        },
        {
          when: (row) => row.status === 'rejected',
          style: {
            color: 'var(--zocks-danger)',
          },
        },
      ],
    },
    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) =>
        ['pending', 'active'].includes(row.status) ? (
          <ActionButton>
            <ScopedBlock
              scope={{
                scope: 'consoleuser:invite',
                superScope: ['consoleuser:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditGrant(row)}>
                Edit
              </Dropdown.Item>
            </ScopedBlock>
            <ScopedBlock
              scope={{
                scope: 'consoleuser:invite',
                superScope: ['consoleuser:admin'],
              }}
              missingScopeAction={MissingScopeAction.DISABLE}
            >
              <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeleteGrant(row)}>
                Delete
              </Dropdown.Item>
            </ScopedBlock>
          </ActionButton>
        ) : null,
    },
  ]

  return (
    <Container>
      {selectedGrantId ? <EditGrantModal grantId={selectedGrantId} show={showEditGrantModal} onHide={handleEditGrantModalHide} /> : null}
      {selectedGrantId ? (
        <DeleteGrantModal grant={data?.data.find((grant) => grant.id === selectedGrantId)} show={showDeleteGrantModal} onHide={handleDeleteGrantModalHide} />
      ) : null}
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={data?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={data?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
